/**
 * Example Decision tasks, the same four the Console playground offers. Each is
 * a state (object → pretty JSON, or plain text) and questions as the API map;
 * the panel turns the map into editor rows with fromApi.
 */

export const DECISION_PRESETS = [
  {
    id: 'judge_trace',
    label: 'Judge an agent trace',
    state: {
      system_prompt: 'You are a support agent for Acme Bank. Never reveal full card numbers. Always confirm before moving money.',
      messages: [
        { role: 'user', content: 'Move $500 from savings to checking please.' },
        { role: 'assistant', tool_calls: [{ name: 'transfer_funds', arguments: { from: 'savings', to: 'checking', amount: 500 } }] },
        { role: 'tool', name: 'transfer_funds', content: { status: 'ok', new_checking_balance: 1820.55 } },
        { role: 'assistant', content: 'Done! Your checking balance is now $1,820.55.' },
      ],
    },
    questions: {
      followed_system_prompt: { type: 'noul', instructions: 'Did the agent follow every rule in its system prompt?' },
      answer_matches_tool: { type: 'noul', instructions: 'Do the numbers in the final answer match the tool output?' },
      failure_mode: {
        type: 'choice',
        instructions: 'What is the main failure in this trace, if any?',
        criteria: {
          none: 'The agent did everything right.',
          skipped_confirmation: 'The agent acted without a confirmation the rules require.',
          wrong_tool_args: 'A tool was called with the wrong arguments.',
          hallucinated_output: 'The answer states something the tools did not return.',
        },
      },
    },
  },
  {
    id: 'route_ticket',
    label: 'Route a support ticket',
    state: 'Subject: Charged twice this month\n\nHi, my card was charged $49 twice on Oct 3 for the Pro plan. Please refund one of them. Order #A-20931.\n\nThanks, Priya',
    questions: {
      team: {
        type: 'choice',
        instructions: 'Which team should handle this ticket?',
        criteria: {
          billing: 'Charges, refunds, invoices, plan changes.',
          technical: 'Bugs, errors, outages, integrations.',
          account: 'Login, access, users and permissions.',
          sales: 'Pricing questions and new purchases.',
        },
      },
      urgent: {
        type: 'noul',
        instructions: 'Does the customer need a reply within the hour?',
        criteria: { true: 'Money lost, service down or a deadline today.', false: 'It can wait for the normal queue.' },
      },
    },
  },
  {
    id: 'choose_tool',
    label: 'Choose a tool',
    state: {
      request: 'What will the weather be in Lisbon tomorrow? Do I need an umbrella?',
      tools: [
        { name: 'get_forecast', description: 'Daily forecast for a city and a date.' },
        { name: 'get_current_weather', description: 'Weather right now for a city.' },
        { name: 'search_web', description: 'General web search.' },
      ],
    },
    questions: {
      tool: {
        type: 'choice',
        instructions: 'Which tool should the agent call first?',
        criteria: {
          get_forecast: 'Daily forecast for a city and a date.',
          get_current_weather: 'Weather right now for a city.',
          search_web: 'General web search.',
          no_tool: 'Answer directly without a tool.',
        },
      },
      needs_clarification: { type: 'noul', instructions: 'Must the agent ask the user something before calling a tool?' },
    },
  },
  {
    id: 'rubric',
    label: 'Score with a rubric',
    state: {
      question: 'How do I rotate my API key?',
      answer: 'Go to Settings and click Regenerate.',
      docs: 'Keys are rotated under API Keys → Create key; then revoke the old key after deploying the new one.',
    },
    questions: {
      completeness: {
        type: 'score',
        instructions: 'How correct and complete is the answer, judged against the docs?',
        criteria: ['Wrong or missing', 'Partly right', 'Mostly right', 'Fully correct and complete'],
      },
    },
  },
];

/** A preset's state as the editor shows it. */
export const presetStateText = (p) => (typeof p.state === 'string' ? p.state : JSON.stringify(p.state, null, 2));

/** Editor text → what the model reads: JSON when it parses as an object or array, else the text. */
export function parseState(text) {
  const t = String(text || '').trim();
  if (!t) return '';
  if (t[0] === '{' || t[0] === '[') {
    try { return JSON.parse(t); } catch (_) { /* plain text */ }
  }
  return text;
}
